import React from 'react'
import { Table, useMediaSize } from '@ally/metronome-ui'
import * as accountSelector from '@ally/data-selectors-accounts'
import { DepositAccount, BoosterSummary } from '@ally/data-types-accounts'
import AccountGroup from '../../Shared/AccountGroup'
import AccountCard from '../../Shared/AccountCard'
import { useHostServices } from '../../HostServices'
import { getColumns } from '../TableColumns'
import MobileDepositAccountList from '../../Shared/MobileAccountsCard/MobileDepositAccountList'
import AccountCardMbox from '../../Shared/AccountCardMbox'
import {
  filterActiveDepositAccounts,
  hasStkSummary,
  sortDepositAccountsGroup,
  hasStaleAccounts,
} from '../../utils'
import { NonPrintingWrapper } from '../../Shared/NonPrintingWrapper'
import { SdaWithDisabledSsBoosterNotif } from '../../notifications'
import { useCustomizeDashboard } from '../../CustomizeDashboard/CustomizeDashboardContext/CustomizeDashboardContext'
import { useAccountTypes } from '../../AccountTypeContext'

const Savings: React.FC = props => {
  const { actionBar, featureFlags, hostData } = useHostServices()
  const [{ dashboardView }] = useCustomizeDashboard()
  const { isBankOutage, activeDepositAccounts } = useAccountTypes()
  const hasStaleData = hasStaleAccounts(activeDepositAccounts)
  const mmaAccountsGroup = sortDepositAccountsGroup(
    accountSelector.deposit.nonRetirement.getMMA(hostData) || [],
  )
  const sdaAccountsGroup = sortDepositAccountsGroup(
    accountSelector.deposit.nonRetirement.getSDA(hostData) || [],
  )
  const cmgAccountsGroup = sortDepositAccountsGroup(
    accountSelector.deposit.nonRetirement.getCMG(hostData) || [],
  )
  const sortedSavingAccounts: DepositAccount[] = [
    ...mmaAccountsGroup.activeAccounts,
    ...sdaAccountsGroup.activeAccounts,
    ...cmgAccountsGroup.activeAccounts,
    ...mmaAccountsGroup.closedAccounts,
    ...sdaAccountsGroup.closedAccounts,
    ...cmgAccountsGroup.closedAccounts,
  ]
  const activeAccounts = filterActiveDepositAccounts(sortedSavingAccounts)
  const activeSdas = filterActiveDepositAccounts([
    ...sdaAccountsGroup.activeAccounts,
  ])
  const isMdDown = useMediaSize('MdDown')
  const isLgDown = useMediaSize('LgDown')
  const isPrint = window.matchMedia('print').matches

  // TODO: Add coverage for disabled FAS Booster
  /* istanbul ignore next */
  const isDisabledSSBooster = (booster: BoosterSummary): boolean => {
    const isSurpriseSavings = booster.boosterType === 'FAS'
    const isDisabled = booster.boosterStatus.toLowerCase() === 'disabled'
    return isSurpriseSavings && isDisabled
  }

  // get the first savings account that has a disabled ss booster, else undefined
  // TODO: Add coverage for disabled SDA booster
  /* istanbul ignore next */
  const sdaWithDisabledSSBooster = activeSdas.find(account => {
    if (hasStkSummary(account)) {
      const boosterSummary = account?.stkSummary?.boosterSummary
      return boosterSummary ? boosterSummary.some(isDisabledSSBooster) : false
    }
    return undefined
  })

  const plaidKillSwitch = featureFlags.variation('FF_plaid-killswitch', false)
  const plaidAggregation = featureFlags.variation('FF_plaid-aggregation', false)

  // notify user if they have an SDA with a disabled surprise savings booster
  React.useEffect(() => {
    // check feature flags
    if (!plaidKillSwitch && plaidAggregation) {
      // TODO - stub action bar from host services and a disabled stk booster
      /* istanbul ignore next */
      if (actionBar && sdaWithDisabledSSBooster) {
        const to = `/bank/accounts/details/${sdaWithDisabledSSBooster.accountId}`
        actionBar.notifications.set([
          {
            type: 'REMOTE',
            innerContent: SdaWithDisabledSsBoosterNotif(to),
          },
        ])
      }
    }
  }, [actionBar, plaidKillSwitch, plaidAggregation, sdaWithDisabledSSBooster])

  return (
    <>
      {activeAccounts.length && !isBankOutage ? (
        <AccountGroup name="savings">
          <AccountCard type="SDA">
            {isMdDown && !isPrint ? (
              <MobileDepositAccountList
                accounts={activeAccounts}
                hasStaleData={hasStaleData}
              />
            ) : (
              <Table
                className="savings-table"
                data={activeAccounts}
                columns={getColumns({
                  tableType: 'Savings',
                  view: dashboardView,
                  isLgDown,
                  hasStaleData,
                })}
                captionText="Information about your Ally Bank accounts"
                fullWidth
                suppressMdDownLayout
                withFooter
                autoResetExpanded={false}
              />
            )}
            <NonPrintingWrapper>
              <AccountCardMbox name="ally-dashboard-savings-well" />
            </NonPrintingWrapper>
          </AccountCard>
        </AccountGroup>
      ) : null}
    </>
  )
}

export default Savings
